import http from "@/services/http";
import { featureFlags } from "@/config/env";
import { mockManager } from "@/services/mock";

/**
 * AI模型信息接口
 */
export interface AIModel {
  id: string;
  ename: string;
  name: string;
  icon: string;
}

/**
 * 模型列表响应接口
 */
export interface ModelsListResponse {
  code: number;
  message: string;
  data: AIModel[];
}

/**
 * 模型列表API
 */
export const modelsApi = {
  /**
   * 获取所有启用且未删除的AI模型列表
   * @returns Promise<AIModel[]> 模型列表
   */
  async getModelsList(): Promise<AIModel[]> {
    try {
      // Mock（PUBLIC_ENABLE_MOCK=true 时生效）
      if (featureFlags.enableMock) {
        return await mockManager.getModels();
      }

      const response = await http.get<ModelsListResponse>("data/models/list");

      console.log("Models API response:", response);

      // 检查响应结构
      if (!response || !response.data) {
        console.error("No response data:", response);
        return [];
      }

      const data = response.data;
      console.log("Models response data:", data);

      // 检查数据结构
      if (Array.isArray(data)) {
        // 如果 data 直接是数组
        return data;
      } else if (data.data && Array.isArray(data.data)) {
        // 如果 data.data 是数组
        return data.data;
      } else {
        console.error("Invalid models API response structure:", data);
        return [];
      }
    } catch (error) {
      // 错误处理
      console.error("Models API error:", error);

      // 如果是API业务错误，错误信息已经在HTTP拦截器中显示
      // 这里返回空数组，避免重复显示错误信息
      return [];
    }
  },

  /**
   * 根据模型ID获取模型信息
   * @param modelId 模型ID
   * @returns Promise<AIModel | null> 模型信息
   */
  async getModelById(modelId: string): Promise<AIModel | null> {
    try {
      const models = await this.getModelsList();
      return models.find((model) => model.id === modelId) || null;
    } catch (error) {
      console.error("Get model by ID error:", error);
      return null;
    }
  },

  /**
   * 根据模型英文名获取模型信息
   * @param ename 模型英文名
   * @returns Promise<AIModel | null> 模型信息
   */
  async getModelByEname(ename: string): Promise<AIModel | null> {
    try {
      const models = await this.getModelsList();
      return models.find((model) => model.ename === ename) || null;
    } catch (error) {
      console.error("Get model by ename error:", error);
      return null;
    }
  },

  /**
   * 根据模型中文名获取模型信息
   * @param name 模型中文名
   * @returns Promise<AIModel | null> 模型信息
   */
  async getModelByName(name: string): Promise<AIModel | null> {
    try {
      const models = await this.getModelsList();
      return models.find((model) => model.name === name) || null;
    } catch (error) {
      console.error("Get model by name error:", error);
      return null;
    }
  },

  /**
   * 搜索模型（支持中英文名模糊搜索）
   * @param keyword 搜索关键词
   * @returns Promise<AIModel[]> 匹配的模型列表
   */
  async searchModels(keyword: string): Promise<AIModel[]> {
    try {
      const models = await this.getModelsList();
      const lowerKeyword = keyword.toLowerCase();

      return models.filter(
        (model) =>
          model.name.toLowerCase().includes(lowerKeyword) ||
          model.ename.toLowerCase().includes(lowerKeyword)
      );
    } catch (error) {
      console.error("Search models error:", error);
      return [];
    }
  },

  /**
   * 获取模型选项列表（用于下拉框等UI组件）
   * @returns Promise<Array<{value: string, label: string, icon?: string}>> 模型选项列表
   */
  async getModelOptions(): Promise<
    Array<{ value: string; label: string; icon?: string }>
  > {
    try {
      const models = await this.getModelsList();
      return models.map((model) => ({
        value: model.id,
        label: model.name,
        icon: model.icon,
      }));
    } catch (error) {
      console.error("Get model options error:", error);
      return [];
    }
  },

  /**
   * 检查模型是否存在
   * @param modelId 模型ID
   * @returns Promise<boolean> 模型是否存在
   */
  async isModelExists(modelId: string): Promise<boolean> {
    try {
      const model = await this.getModelById(modelId);
      return model !== null;
    } catch (error) {
      console.error("Check model exists error:", error);
      return false;
    }
  },

  /**
   * 获取所有模型ID列表
   * @returns Promise<string[]> 模型ID列表
   */
  async getModelIds(): Promise<string[]> {
    try {
      const models = await this.getModelsList();
      return models.map((model) => model.id);
    } catch (error) {
      console.error("Get model IDs error:", error);
      return [];
    }
  },

  /**
   * 获取所有模型名称列表
   * @returns Promise<string[]> 模型名称列表
   */
  async getModelNames(): Promise<string[]> {
    try {
      const models = await this.getModelsList();
      return models.map((model) => model.name);
    } catch (error) {
      console.error("Get model names error:", error);
      return [];
    }
  },
};

export default modelsApi;
